import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  Box,
  Typography,
  Grid,
  Divider,
  Chip,
  Container,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { getArticleByid } from "../../../store/api/content";
import Loading from "../../common/Loading";
import Button from "../../common/Button";
import { format } from "date-fns";
import ArticleResources from "../Dashboard/ArticleDetails/ArticleResource";
import { useOrganisation } from "../../../hooks/useOrganisation";
import { roles } from "../../../constants/routes";
import { getRole } from "../../../helpers/auth";
import { IMAGE_URL_AZURE } from "../../../constants/env";

const PreviewContent = ({ open, handleClose, id }) => {
  const navigate = useNavigate();
  const { primaryColor } = useOrganisation();
  const role = getRole();
  const companyName = localStorage.getItem("companyName");
  const [processedContent, setProcessedContent] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["article", id],
    queryFn: () =>
      getArticleByid({
        id,
      }),
    enabled: !!id,
  });

  // Function to process HTML content and resize media elements
  useEffect(() => {
    if (data?.text) {
      // Create a temporary div to parse the HTML
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = data.text;

      // Find all images in the content
      const images = tempDiv.querySelectorAll("img");
      images.forEach((img) => {
        // Set responsive image styles
        img.style.maxWidth = "100%";
        img.style.height = "auto";
        img.style.display = "block";
        img.style.margin = "16px auto"; // Center align with margin
        img.style.borderRadius = "8px";
        img.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
      });

      // Find all videos in the content
      const videos = tempDiv.querySelectorAll("video");
      videos.forEach((video) => {
        // Set responsive video styles
        video.style.maxWidth = "100%";
        video.style.height = "auto";
        video.style.display = "block";
        video.style.margin = "16px auto"; // Center align with margin
        video.style.borderRadius = "8px";
        video.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
        video.setAttribute("controls", "true"); // Ensure controls are visible
      });

      // Find all iframes (for embedded videos like YouTube)
      const iframes = tempDiv.querySelectorAll("iframe");
      iframes.forEach((iframe) => {
        // Create responsive wrapper for iframes
        const wrapper = document.createElement("div");
        wrapper.style.position = "relative";
        wrapper.style.paddingBottom = "56.25%"; // 16:9 aspect ratio
        wrapper.style.height = "0";
        wrapper.style.overflow = "hidden";
        wrapper.style.margin = "16px auto";
        wrapper.style.borderRadius = "8px";
        wrapper.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";

        iframe.style.position = "absolute";
        iframe.style.top = "0";
        iframe.style.left = "0";
        iframe.style.width = "100%";
        iframe.style.height = "100%";
        iframe.style.border = "none";

        // Wrap the iframe
        iframe.parentNode.insertBefore(wrapper, iframe);
        wrapper.appendChild(iframe);
      });

      setProcessedContent(tempDiv.innerHTML);
    }
  }, [data?.text]);

  const shouldShowEditButton =
    (roles.ContentCreator === role && data?.status === "Pending") ||
    roles.ContentCreator !== role;

  const handleFileClick = (file) => {
    if (
      file.contentType.startsWith("image/") ||
      file.contentType.startsWith("video/")
    ) {
    } else {
      window.open(file.url + `?${IMAGE_URL_AZURE}`, "_blank");
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Container maxWidth="lg" sx={{ padding: 4 }}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 900,
            maxHeight: "90vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            overflowY: "auto",
          }}
        >
          {isLoading ? (
            <Loading />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h4" component="h2" gutterBottom>
                  {data?.title}
                </Typography>
                <Typography
                  variant="subtitle1"
                  color="textSecondary"
                  gutterBottom
                >
                  {data?.author || ""}{" "}
                  {data?.scheduledDate
                    ? format(new Date(data.scheduledDate), "dd MMM yyyy")
                    : "N/A"}
                </Typography>
              </Grid>
              <Grid container item xs={12} display="flex" alignItems="start">
                <Grid item xs={12} md={6}>
                  <img
                    src={`${data?.mainImage}?${IMAGE_URL_AZURE}`}
                    alt="Preview"
                    style={{ width: "300px", height: "auto" }}
                  />
                  <Box sx={{ marginTop: 2 }}>
                    {data?.topic?.split(",").map((el, i) => (
                      <Chip
                        key={i}
                        label={el}
                        style={{
                          marginRight: 4,
                          marginBottom: 4,
                          color: "black",
                          background: "white",
                          border: `1px solid ${primaryColor}`,
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  display="flex"
                  flexDirection="column"
                  justifyContent="start"
                  alignItems={"start"}
                  padding={2}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      marginBottom: 3,
                      "& img": {
                        maxWidth: "100%",
                        height: "auto",
                        display: "block",
                        margin: "16px auto",
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      },
                      "& video": {
                        maxWidth: "100%",
                        height: "auto",
                        display: "block",
                        margin: "16px auto",
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      },
                      "& iframe": {
                        maxWidth: "100%",
                        margin: "16px auto",
                        display: "block",
                        borderRadius: "8px",
                      },
                    }}
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: processedContent || data?.text,
                      }}
                    />
                  </Typography>
                  {data?.url && (
                    <Button
                      variant="contained"
                      text="View Content'"
                      to={data?.url}
                      linkExternal={true}
                    />
                  )}
                  {data?.files && data.files.length > 0 && (
                    <Box className="mt-4">
                      <Typography
                        variant="subtitle2"
                        className="text-gray-600 mb-2"
                      >
                        Related Files:
                      </Typography>
                      <ArticleResources
                        files={data.files}
                        onFileClick={handleFileClick}
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mt: 2, display: "flex", justifyContent: "end" }}>
                  <Button
                    text="Close"
                    onClick={handleClose}
                    backgroundColor="white"
                    style={{ border: "1px solid black" }}
                  />
                  {shouldShowEditButton && (
                    <Button
                      text="Edit content"
                      onClick={() =>
                        navigate(`/${companyName}/edit-content/${id}`)
                      }
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </Box>
      </Container>
    </Modal>
  );
};

export default PreviewContent;
